import {Observable} from 'rxjs';
import {authorizedFetch} from '../core/authorizedFetch';
import axios from "axios";
import {ConversationData, ConversationDetailResponse} from "./conversation.service";
import {env} from "../config/environment";

const BASE_URL = env.backendUrl;

export interface TranslatorMessageParams {
    conversationId: string;
    text: string;
    lang: string;
    fromLang: string | null;
    toLang: string | null;
    retry?: boolean;
}

class TranslatorService {

    // TODO: fetching translations depending on conversation ID
    async fetchTranslation(conversationId: string): Promise<ConversationData> {
        const response =
            await axios.get<ConversationDetailResponse>(`/translation/conversation/${conversationId}`);
        const data = response.data;

        return {
            id: data.id ?? parseInt(conversationId, 10),
            title: data.title ?? '',
            messages: [],
            answerVariants: [],
            selectedGroupIds: [],
            selectedDocIds: [],
            selectedWholeCollectionIds: [],
            selectedModelId: null,
            searchInternet: false,
            documents: data.documents || [],
        };
    }

    async createTranslationConversation(): Promise<string> {
        const response = await axios.post<{ id: string }>('/translation/conversation');
        return response.data.id;
    }

    createMessageStream(params: TranslatorMessageParams): Observable<string> {
        const { conversationId, text, lang, fromLang, toLang, retry } = params;

        return new Observable<string>(subscriber => {
            if (
                !conversationId ||
                String(conversationId) === 'undefined' ||
                String(conversationId) === 'null'
            ) {
                subscriber.error(new Error('Invalid conversationId: ' + conversationId));
                return;
            }

            const controller = new AbortController();
            let readerReleased = false;

            const releaseReader = (reader: ReadableStreamDefaultReader<Uint8Array> | null) => {
                if (readerReleased || !reader) return;
                readerReleased = true;
                try {
                    reader.releaseLock();
                } catch (e) {
                    console.error(e);
                }
            };

            const startStream = async () => {
                try {
                    // safe way to send sensitive and long data (browsers and servers set limits on URL length)
                    const formData = new FormData();

                    formData.append('text', text);
                    formData.append('conversationId', conversationId);
                    formData.append('lang', lang);
                    formData.append('fromLang', fromLang || '');
                    formData.append('toLang', toLang || '');
                    formData.append('retry', retry ? 'true' : 'false');

                    const url = `${BASE_URL}/translation`;

                    const response = await authorizedFetch(url, {
                        method: 'POST',
                        headers: {
                            Accept: 'text/event-stream',
                        },
                        body: formData,
                        signal: controller.signal,
                    });

                    if (!response.ok) {
                        const errorText = await response.text();
                        throw new Error(`HTTP ${response.status}: ${errorText}`);
                    }

                    if (!response.body) {
                        throw new Error('Response body is empty');
                    }

                    const reader = response.body.getReader();
                    const decoder = new TextDecoder();
                    let buffer = '';

                    try {
                        let reading = true;
                        while (reading) {
                            const { done, value } = await reader.read();
                            if (done) {
                                reading = false;
                                break;
                            }

                            // Todo: Refactor or delete! This causes format failure as it mimics Markdown instead of combining
                            //  markdown chunks to one string.
                            buffer += decoder.decode(value, { stream: true });

                            let idx: number;
                            while ((idx = buffer.indexOf('\n\n')) !== -1) {
                                const rawEvent = buffer.slice(0, idx);
                                buffer = buffer.slice(idx + 2);

                                const lines = rawEvent.split(/\r?\n/);
                                let dataPayload = '';
                                for (const line of lines) {
                                    const m = line.match(/^data:(.*)$/);
                                    if (m) dataPayload += m[1];
                                }

                                if (dataPayload === '') {
                                    subscriber.next('\n');
                                } else if (dataPayload === '_END_') {
                                    releaseReader(reader);
                                    subscriber.complete();
                                    return;
                                } else {
                                    subscriber.next(dataPayload);
                                }
                            }
                        }

                        releaseReader(reader);
                        subscriber.complete();
                    } catch (error) {
                        releaseReader(reader);
                        if (!controller.signal.aborted) {
                            subscriber.error(error);
                        }
                    }
                } catch (error) {
                    if (!controller.signal.aborted) {
                        subscriber.error(error);
                    }
                }
            };

            startStream();

            return () => {
                controller.abort();
            };
        });
    }
}

export const translatorService = new TranslatorService();
